import { Dropdown, Menu, Message, Modal } from '@arco-design/web-react';
import { IconClockCircle, IconDown, IconLock, IconPoweroff } from '@arco-design/web-react/icon';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/auth';
import { useAgentStore } from '../../store/agent';
import { useShiftStore } from '../../store/shift';
import { useViewRole } from '../../hooks/useViewRole';
import { statusLabel } from '../../utils/labels';

/** 演示班次：产品口径只要求展示写死的 08:00–17:00；换真实今日班次（`GET /shift/me/today` 已有封装）时只改这一处。 */
const DEMO_SHIFT = '08:00–17:00';

/**
 * 顶栏用户信息块：角色首字圆头像（管/主/坐，不取姓名）+ 姓名单行；
 * 坐席在姓名右侧用分机号胶囊（非坐席不显示账号）。
 * 下拉：班次信息行（只读） / 修改密码 / 上班（下班） / 退出登录。
 * 班次时段只在菜单顶部那一行，不占顶栏——顶栏那簇是动作与状态切换，只读信息不进去。
 * （登出前置坐席离线，best-effort——否则状态残留 idle/break，ACD 继续派单）。
 * 「界面设置」入口不在此下拉：统一收在侧栏底栏（layouts/RegistrySidebar），抽屉实例只挂 BasicLayout 一处。
 */
export default function HeaderUser() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const agent = useAgentStore();
  const { logout, openPwdModal } = useAuthStore();
  const viewRole = useViewRole();
  const onDuty = useShiftStore((s) => s.onDuty);
  const setOnDuty = useShiftStore((s) => s.setOnDuty);
  const showShift = viewRole !== 'admin';
  const displayName = (user?.name || user?.username || '').trim();
  const extension = (agent.extension || user?.extension || '').trim();
  const isAgent = Boolean(agent.agentId || extension);
  const roleKey = user?.role || (isAgent ? 'agent' : '');
  // 角色中文在 STATUS_LABELS（管理员/主管/坐席）；builtinLabel 不覆盖角色码，admin/agent 会原样返回变成「A」
  const roleLabel = statusLabel(roleKey);
  const avatarChar = roleLabel && roleLabel !== '-' ? roleLabel.charAt(0) : '?';

  const userMenu = (
    <Menu
      onClickMenuItem={async (key) => {
        if (key === 'logout') {
          // 登出前置离线（best-effort，不阻塞登出）：否则坐席状态残留 idle/break，
          // ACD 会继续向该坐席派单（Redis 状态无 TTL，重启也清不掉）
          if (agent.agentId && agent.state !== 'offline') {
            try {
              await agent.changeState('offline');
            } catch {
              /* 忽略：登出不因状态接口失败而中断 */
            }
          }
          logout();
          navigate('/login');
        } else if (key === 'change-pwd') {
          // 个人改密入口（W5-4）：同一全局 Modal，可关闭
          openPwdModal(false);
        } else if (key === 'shift') {
          if (onDuty) {
            Modal.confirm({
              title: '确认下班？',
              content: '下班后将停止派单（演示，不会真的改状态）。',
              okText: '下班',
              cancelText: '取消',
              onOk: () => {
                setOnDuty(false);
                Message.success('已下班（演示）');
              },
            });
          } else {
            setOnDuty(true);
            Message.success('已上班（演示）');
          }
        }
      }}
    >
      {/* 班次信息行（只读）：Dropdown 只认「droplist 本身就是 Menu」才注入点选后收起，
          所以这一行放在 Menu 下当普通 div（Menu 会克隆它、多带 _key/level 属性，无影响）。 */}
      {showShift ? (
        <div className="bcc-user-shift" title="当前班次（演示数据）">
          <IconClockCircle />
          <span className="bcc-user-shift-range">班次 {DEMO_SHIFT}</span>
          <span className={`bcc-user-shift-state${onDuty ? ' is-on' : ''}`}>{onDuty ? '在班' : '未上班'}</span>
        </div>
      ) : null}
      <Menu.Item key="change-pwd">
        <IconLock style={{ marginRight: 8 }} />
        修改密码
      </Menu.Item>
      {showShift ? (
        <Menu.Item key="shift">
          <IconClockCircle style={{ marginRight: 8 }} />
          {onDuty ? '下班' : '上班'}
        </Menu.Item>
      ) : null}
      <Menu.Item key="logout">
        <IconPoweroff style={{ marginRight: 8 }} />
        退出登录
      </Menu.Item>
    </Menu>
  );

  // 点击触发（菜单一律 click，见 MoreActions 的同条口径）：hover 既有 400ms 悬浮等待，点击又无效
  return (
    <Dropdown trigger="click" droplist={userMenu} position="br">
      <div className="bcc-user-chip" style={{ cursor: 'pointer' }}>
        <span className="bcc-user-avatar">{avatarChar}</span>
        <span className="bcc-user-name">
          <b>{displayName}</b>
          {isAgent && extension ? <span className="bcc-user-ext">{extension}</span> : null}
        </span>
        <IconDown className="bcc-user-arrow" />
      </div>
    </Dropdown>
  );
}
